import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash2, ClipboardList, Info } from "lucide-react";
import { formatINR } from "@/lib/permissions";
import { useBranches } from "@/hooks/useBranches";

const PRIORITIES = [
  { code: "low", label: "Low" },
  { code: "normal", label: "Normal" },
  { code: "high", label: "High" },
  { code: "urgent", label: "Urgent" },
];

const newLine = () => ({ id: crypto.randomUUID(), consumable_id: "", qty_requested: 1, note: "" });

export default function ConsumableRequestNew() {
  const navigate = useNavigate();
  const { branches } = useBranches();
  const [consumables, setConsumables] = useState([]);
  const [lines, setLines] = useState([newLine()]);
  const [priority, setPriority] = useState("normal");
  const [neededBy, setNeededBy] = useState("");
  const [justification, setJustification] = useState("");
  const [destinationBranchId, setDestinationBranchId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/consumables").then(({ data }) => setConsumables(data || [])).catch(() => {});
  }, []);

  // Auto-select the destination branch as soon as we know which branches the
  // requester is allowed to raise into. Single-branch users skip a click;
  // multi-branch users still see the picker and can override.
  useEffect(() => {
    if (branches?.length && !destinationBranchId) {
      setDestinationBranchId(branches[0].id);
    }
  }, [branches, destinationBranchId]);

  const updateLine = (id, field, value) =>
    setLines((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const addLine = () => setLines((rows) => [...rows, newLine()]);
  const removeLine = (id) => setLines((rows) => (rows.length > 1 ? rows.filter((r) => r.id !== id) : rows));

  const total = lines.reduce((s, l) => {
    const c = consumables.find((x) => x.id === l.consumable_id);
    return s + (Number(l.qty_requested) || 0) * (Number(c?.unit_cost) || 0);
  }, 0);

  const submit = async (e) => {
    e.preventDefault();
    const items = lines
      .filter((l) => l.consumable_id && Number(l.qty_requested) > 0)
      .map((l) => ({
        consumable_id: l.consumable_id,
        qty_requested: Number(l.qty_requested),
        note: (l.note || "").trim() || null,
      }));
    if (items.length === 0) {
      toast.error("Add at least one consumable with quantity");
      return;
    }
    if (!destinationBranchId) {
      toast.error("Pick which branch will receive the stock");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/consumable-requests", {
        items,
        priority,
        needed_by: neededBy || null,
        justification: justification || null,
        destination_branch_id: destinationBranchId,
      });
      toast.success(`Requisition ${data.request_number} raised — approvers notified`);
      navigate(`/consumable-requests/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to raise request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-request-page">
      <button
        onClick={() => navigate("/consumable-requests")}
        className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Requisitions
      </button>

      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">New</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
          <ClipboardList className="w-7 h-7" /> Raise Consumable Requisition
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Pick items from the consumable master. An approver will review and, if approved, raise a PO to your vendor.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base font-heading">Request Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Destination Branch <span className="text-rose-500">*</span></Label>
              <select
                value={destinationBranchId}
                onChange={(e) => setDestinationBranchId(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm mt-1"
                data-testid="request-destination-branch"
                required
              >
                {(!branches || branches.length === 0) && (
                  <option value="">— Loading branches —</option>
                )}
                {branches?.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">
                Stock will be delivered to and stocked at this branch after approval &amp; receipt.
              </div>
            </div>
            <div>
              <Label>Priority</Label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm mt-1"
                data-testid="request-priority"
              >
                {PRIORITIES.map((p) => (<option key={p.code} value={p.code}>{p.label}</option>))}
              </select>
            </div>
            <div>
              <Label>Needed by</Label>
              <Input
                type="date"
                value={neededBy}
                onChange={(e) => setNeededBy(e.target.value)}
                data-testid="request-needed-by"
              />
            </div>
            <div className="md:col-span-3">
              <Label>Justification / Notes</Label>
              <Textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Why is this required? Which machine / job / department?"
                rows={2}
                data-testid="request-justification"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading">Line Items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="add-request-line">
              <Plus className="w-4 h-4 mr-1" /> Add item
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {lines.map((l, idx) => {
              const master = consumables.find((c) => c.id === l.consumable_id);
              const stock = master ? Number(master.current_stock) || 0 : 0;
              const lowStock = master && stock < (Number(master.reorder_level) || 0);
              const est = (Number(l.qty_requested) || 0) * (Number(master?.unit_cost) || 0);
              return (
                <div key={l.id} className="grid grid-cols-12 gap-2 items-start p-3 bg-slate-50 rounded-md" data-testid={`request-line-${idx}`}>
                  <div className="col-span-12 md:col-span-6">
                    <Label className="text-[10px] uppercase text-slate-500">Consumable</Label>
                    <select
                      value={l.consumable_id}
                      onChange={(e) => updateLine(l.id, "consumable_id", e.target.value)}
                      className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm"
                      data-testid={`request-line-${idx}-select`}
                    >
                      <option value="">— Select consumable —</option>
                      {consumables.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.code} · {c.name} (stock {c.current_stock} {c.unit_of_measure} @ ₹{c.unit_cost || 0})
                        </option>
                      ))}
                    </select>
                    {master && (
                      <div className={`text-[11px] mt-1 flex items-center gap-1 ${lowStock ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                        <Info className="w-3 h-3" />
                        Current stock: {stock} {master.unit_of_measure}
                        {master.reorder_level != null && ` · reorder at ${master.reorder_level}`}
                        {lowStock && " · BELOW REORDER"}
                      </div>
                    )}
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Label className="text-[10px] uppercase text-slate-500">Qty requested</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={l.qty_requested}
                      onChange={(e) => updateLine(l.id, "qty_requested", e.target.value)}
                      className="text-right"
                      data-testid={`request-line-${idx}-qty`}
                    />
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <Label className="text-[10px] uppercase text-slate-500">Note (optional)</Label>
                    <Input
                      value={l.note}
                      onChange={(e) => updateLine(l.id, "note", e.target.value)}
                      placeholder="e.g. urgent for CNC-01"
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1 pt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeLine(l.id)}
                      disabled={lines.length === 1}
                      className="text-red-500 hover:text-red-700 disabled:opacity-30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {master && (
                    <div className="col-span-12 text-right text-xs text-slate-500 -mt-1">
                      Est. value: <span className="font-mono font-semibold text-slate-800">{formatINR(est)}</span>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex justify-end pt-2 border-t border-slate-100 text-sm font-semibold">
              <span className="text-slate-500 mr-4">Estimated total:</span>
              <span className="font-mono text-lg text-[#7C1D3A]" data-testid="request-est-total">{formatINR(total)}</span>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/consumable-requests")}>Cancel</Button>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
            data-testid="submit-request-btn"
          >
            {submitting ? "Submitting…" : "Raise Requisition"}
          </Button>
        </div>
      </form>
    </div>
  );
}
